// Schema.org structured-data helpers.
// IMPORTANT: do NOT wrap the JSON-LD <script> in a custom React component —
// react-helmet-async only walks NATIVE DOM children. Pass the object(s)
// returned by these helpers to <Seo jsonLd={…} /> instead.

const SITE_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "https://ttiholidays.com";

/** Build a Schema.org BreadcrumbList from an ordered crumbs array. */
export function breadcrumbSchema(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.path?.startsWith("http") ? c.path : `${SITE_ORIGIN}${c.path}`,
    })),
  };
}

/** Schema.org TravelAgency / Organization data for the homepage. */
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    name: "Tour Trips India Holidays",
    alternateName: "TTI Holidays",
    url: SITE_ORIGIN + "/",
    logo:
      "https://customer-assets.emergentagent.com/job_india-tour-trips/artifacts/bszk0n38_TTI%20NEW%20PNG.png",
    image:
      "https://customer-assets.emergentagent.com/job_india-tour-trips/artifacts/bszk0n38_TTI%20NEW%20PNG.png",
    description:
      "Family-run inbound tour operator based in Agra, India — curating multilingual tours of India (Golden Triangle, Spiritual, Rajasthan, Wildlife, Festivals) for travellers from Europe, Latin America, the Middle East and worldwide, with seamless extensions to Nepal, Dubai and Thailand. Service in English, Spanish, Portuguese, French, German, Italian, Russian, Arabic and Hindi.",
    foundingLocation: "Agra, Uttar Pradesh, India",
    areaServed: [
      "IN", "NP", "AE", "TH",
      // Europe
      "ES", "PT", "FR", "DE", "IT", "GB", "NL", "BE", "CH", "AT", "SE", "NO", "DK", "FI", "PL", "RU",
      // Latin America
      "MX", "BR", "AR", "CL", "CO", "PE", "UY",
      // North America
      "US", "CA",
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "P-10, Tajnagri Phase 1, Tajganj",
      addressLocality: "Agra",
      postalCode: "282001",
      addressRegion: "Uttar Pradesh",
      addressCountry: "IN",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer service",
        telephone: "+91-70177-98524",
        email: "info@ttiholidays.com",
        availableLanguage: [
          "English", "Hindi", "Spanish", "Portuguese", "French",
          "German", "Italian", "Russian", "Arabic",
        ],
        areaServed: [
          "IN", "NP", "AE", "TH",
          "ES", "PT", "FR", "DE", "IT", "GB", "NL", "BE", "CH", "AT", "RU",
          "MX", "BR", "AR", "CL", "CO", "PE", "US", "CA",
        ],
      },
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "520",
      bestRating: "5",
      worstRating: "1",
    },
    knowsAbout: [
      "Golden Triangle India",
      "Taj Mahal tours",
      "Rajasthan tours",
      "Spiritual India",
      "Wildlife tours India",
      "Diwali festival tours",
      "Holi festival tours",
      "Nepal extensions",
      "Dubai extensions",
      "Thailand extensions",
      "Tours to India for Spanish speakers",
      "Viajes a India",
      "Viagem à Índia",
      "Voyage en Inde",
      "Indien Rundreise",
      "Tour India",
      "India honeymoon packages",
      "India cultural tours",
      "Multilingual tour guides India",
    ],
    sameAs: [
      "https://www.facebook.com/tourtripsindiaagra",
      "https://www.instagram.com/tourtripsindiaholidays/",
      "https://www.linkedin.com/in/tourtripsindiaholidays/",
      "https://www.youtube.com/@tourtripsindiaholidays",
      "https://www.tripadvisor.in/Attraction_Review-g297683-d12391310-Reviews-Tour_Trips_India_Holidays-Agra_Agra_District_Uttar_Pradesh.html",
    ],
  };
}

/** Schema.org WebSite + SitelinksSearchBox — enables Google's search box in SERP. */
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Tour Trips India Holidays",
    alternateName: "TTI Holidays",
    url: SITE_ORIGIN + "/",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: SITE_ORIGIN + "/tours?country={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/** Schema.org FAQPage — wins FAQ rich results in Google. */
export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: { "@type": "Answer", text: q.answer },
    })),
  };
}

/** Schema.org ItemList — for /tours and /destinations grid pages. */
export function itemListSchema({ name, items }) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    numberOfItems: items.length,
    itemListElement: items.slice(0, 30).map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: it.url?.startsWith("http") ? it.url : `${SITE_ORIGIN}${it.url}`,
      name: it.name,
    })),
  };
}

